import type { Route } from 'vue-router';
import { isNaN } from 'lodash-es';
import regexpEscape from 'escape-string-regexp';
import config, { type QueryFilters, type UrlFilters } from '~/lib/filter-params/config';
import type {
  ListQueryVariables,
  ProductProviderKey,
  OfferFilterParams,
  OfferSearchParams,
  PriceFilterParams,
} from '~/lib/goliath/goliath.types';
import { captureException } from '~/lib/sentry';

const toInt = (value: string | undefined) => {
  if (!value) return undefined;
  const num = parseInt(value, 10);
  return isNaN(num) ? undefined : num;
};

const toNumber = (value: string | undefined) => {
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

const patterns = {
  filter: new RegExp(`^${regexpEscape(config.prefixes.filter)}(.+)$`),
  page: new RegExp(`^${regexpEscape(config.prefixes.page)}(\\d+)$`),
  compatibility: new RegExp(
    `^${regexpEscape(config.filter.prefixes.compatibleProductFamilySlugs || '')}(.+)$`
  ),
  category: /^[\w-_]+$/,
  filterValue: /^[\w-_&,]*$/,
};

const isValidSlug = (val: string | undefined) => {
  if (!val) return undefined;
  return /^[\w-_+]+$/.test(val);
};

const isValidFilter = (val: string | undefined) => {
  if (!val) return undefined;
  return /^[\w-_+%,]+$/.test(val);
};

// Splits the Path and checks every segment
const getValidatedPath = (path: string | undefined): string[] => {
  if (!path) return [];
  const segments = path.split(config.separators.path).filter((s) => s.length > 0);
  const validatedSegments = segments.filter(isValidFilter);
  if (segments.length !== validatedSegments.length) {
    throw new Error(
      `Path contains invalid segments. Valid segments: ${validatedSegments}, all segments: ${segments}`
    );
  }
  return validatedSegments;
};

// return just the category path segments
const getCategoryPathSegments = (pathSegments: string[]) => {
  const breakIndex = pathSegments.findIndex(
    (segment) => segment.match(patterns.filter) || segment.match(patterns.page)
  );

  const categorySegments = breakIndex >= 0 ? pathSegments.slice(0, breakIndex) : pathSegments;
  const validatedSegments = categorySegments.filter(isValidSlug);
  if (categorySegments.length !== validatedSegments.length) {
    throw new Error(
      `CategoryPathSegment contains invalid segment(s). Valid segments: ${validatedSegments}, all segments: ${categorySegments}`
    );
  }
  return categorySegments;
};

// returns the filter path segment
const getPathSegmentWithPattern = (pathSegments: string[], pattern: RegExp) => {
  const matchingSegment = pathSegments.find((segment) => {
    const match = segment.match(pattern);
    return match && match[1];
  });

  return matchingSegment && matchingSegment.match(pattern)?.[1];
};

export default class Parser {
  private readonly query: QueryFilters;

  private readonly categoryPathSegments: string[] | undefined = [];

  private readonly pagePathSegment: string | undefined = undefined;

  private readonly filterPathSegment: string | undefined = undefined;

  constructor(route: Route) {
    this.query = route?.query || {};

    if (route?.params?.config) {
      try {
        const pathSegments = getValidatedPath(route.params.config);
        this.categoryPathSegments = getCategoryPathSegments(pathSegments);
        this.filterPathSegment = getPathSegmentWithPattern(pathSegments, patterns.filter);
        this.pagePathSegment = getPathSegmentWithPattern(pathSegments, patterns.page);
      } catch (e) {
        captureException(e, {
          level: 'info',
        });
        // reset everything, if the URL is invalid.
        this.categoryPathSegments = undefined;
        this.pagePathSegment = undefined;
        this.filterPathSegment = undefined;
        this.query = {};
      }
    }
  }

  getVars() {
    // if something invalid is in the url, it is undefined. otherwise it's an (empty) array
    if (this.categoryPathSegments === undefined) {
      return undefined;
    }
    const result: ListQueryVariables = {};

    const complexSearch = this.getComplexSearch();
    const search = this.getSearchTerm();
    const filter = this.getFilterVars();
    const sort = this.getSortVars();
    const pagination = this.getPaginationVars();

    if (complexSearch) {
      result.complexSearch = complexSearch;
    }
    if (search) {
      result.search = search;
    }
    if (filter) {
      result.filter = filter;
    }
    if (sort) {
      result.sort = sort;
    }
    if (pagination) {
      result.pagination = pagination;
    }

    return result;
  }

  // Search

  getSearchTerm() {
    if (!this.query?.search || typeof this.query.search !== 'string') return undefined;
    return this.query.search.trim();
  }

  /**
   * The nginx Proxy Cache can handle sings better than array. So we have to convert the Complex Search to an array.
   */
  getComplexSearch(): OfferSearchParams | undefined {
    const filters = this.query.filters || undefined;
    const notFilters = this.query.notFilters || undefined;
    const orFilters = this.query.orFilters || undefined;
    if (!filters && !notFilters && !orFilters) return undefined;

    let { fields } = this.query;
    if (!fields) {
      fields = ['product.family.name', 'product.name'];
    }

    const offerSearchParams: OfferSearchParams = {
      fields: Array.isArray(fields) ? fields : fields.split(','),
    };
    if (filters) {
      offerSearchParams.filters = Array.isArray(filters) ? filters : filters.split(',');
    }
    if (notFilters) {
      offerSearchParams.notFilters = Array.isArray(notFilters) ? notFilters : notFilters.split(',');
    }
    if (orFilters) {
      offerSearchParams.orFilters = Array.isArray(orFilters) ? orFilters : orFilters.split(',');
    }

    return offerSearchParams;
  }

  // Filters

  getFilterVars(): OfferFilterParams {
    const filter: OfferFilterParams = {};

    const productProviders = this.getProductProviders();
    const brandSlugs = this.getBrandSlugs();
    const colorSlugs = this.getColorSlugs();
    const compatibleProductFamilySlugs = this.getCompatibleProductFamilySlugs();
    const productTagSlugs = this.getProductTagSlugs();
    const anyInGroupOfProductTagSlugs = this.getAnyInGroupOfProductTagSlugs();
    const discounted = this.getDiscountedFilter();
    const onetimePrice = this.getOnetimePriceFilter();
    const productCategoryPaths = this.getProductCategoryPaths();

    if (productProviders.length > 0) {
      filter.productProviders = productProviders;
    }
    if (brandSlugs.length > 0) {
      filter.brandSlugs = brandSlugs;
    }
    if (colorSlugs.length > 0) {
      filter.colorSlugs = colorSlugs;
    }
    if (compatibleProductFamilySlugs.length > 0) {
      filter.compatibleProductFamilySlugs = compatibleProductFamilySlugs;
    }
    if (productTagSlugs.length > 0) {
      filter.productTagSlugs = productTagSlugs;
    }
    if (anyInGroupOfProductTagSlugs.length > 0) {
      filter.anyInGroupOfProductTagSlugs = anyInGroupOfProductTagSlugs;
    }
    if (discounted !== undefined) {
      filter.discounted = discounted;
    }
    if (onetimePrice !== undefined) {
      filter.onetimePrice = onetimePrice;
    }
    if (productCategoryPaths && productCategoryPaths.length > 0) {
      filter.productCategoryPaths = productCategoryPaths;
    }

    return filter;
  }

  getProductProviders(): ProductProviderKey[] {
    const { productProviders } = this.query;
    if (!productProviders) return [];
    if (Array.isArray(productProviders)) return productProviders as ProductProviderKey[];
    return [productProviders];
  }

  getBrandSlugs() {
    return this.getFilterValues('brandSlugs');
  }

  getColorSlugs() {
    return this.getFilterValues('colorSlugs');
  }

  getProductTagSlugs() {
    const { productTagSlugs } = this.query;
    if (!productTagSlugs) return [];
    if (Array.isArray(productTagSlugs)) return productTagSlugs;
    return [productTagSlugs];
  }

  getAnyInGroupOfProductTagSlugs() {
    const { anyInGroupOfProductTagSlugs } = this.query;
    if (!anyInGroupOfProductTagSlugs) return [];
    if (Array.isArray(anyInGroupOfProductTagSlugs)) {
      if (anyInGroupOfProductTagSlugs?.every(Array.isArray)) return anyInGroupOfProductTagSlugs;
      if (anyInGroupOfProductTagSlugs.every((el) => typeof el === 'string'))
        return anyInGroupOfProductTagSlugs.map((group) => group.split(','));
    }
    if (typeof anyInGroupOfProductTagSlugs === 'string')
      return [anyInGroupOfProductTagSlugs.split(',')];
    return [];
  }

  getCompatibleProductFamilySlugs() {
    const compatibilitiesWithPrefix = this.getFilterValue('compatibleProductFamilySlugs');
    if (!compatibilitiesWithPrefix) return [];
    const match = compatibilitiesWithPrefix.match(patterns.compatibility);
    if (!match || !match[1]) return [];
    const [, compatibilities] = match;
    return compatibilities.split(config.separators.item).sort();
  }

  getDiscountedFilter() {
    switch (this.query.discounted) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return undefined;
    }
  }

  getOnetimePriceFilter() {
    const min = toNumber(this.query.minPrice);
    const max = toNumber(this.query.maxPrice);
    const filter: PriceFilterParams = {};
    if (min) filter.min = min;
    if (max) filter.max = max;
    return Object.keys(filter).length > 0 ? filter : undefined;
  }

  getProductCategoryPaths() {
    if (!this.categoryPathSegments) return undefined;

    let paths;

    const categoryPathSegments = this.categoryPathSegments.map((segment) =>
      segment.split(config.separators.item).sort()
    );

    const parentPathSegments: string[] = [];

    for (let i = 0; i < categoryPathSegments.length; i += 1) {
      const isLeafLevel = i === categoryPathSegments.length - 1;
      const items = categoryPathSegments[i];

      if (isLeafLevel) {
        // When reaching the leaf level, we can put the entire category path
        // together.
        paths = items.map((leafPathSegment: string) =>
          parentPathSegments.concat([leafPathSegment]).join(config.separators.path)
        );
      } else {
        if (items.length > 1) {
          // I don't know whether it is completely valid to throw here. Another
          // variant would be to just silently select the first item without
          // throwing. But be aware that not throwing may lead to multiple list
          // pages displaying the exactly same content which is not desirable
          // for SEO.
          throw new Error(
            `Unable to filter by multiple categories on non-leaf level: ${JSON.stringify(
              categoryPathSegments
            )}`
          );
        }

        parentPathSegments.push(items[0]);
      }
    }

    return paths;
  }

  // Pagination

  getFilterPathSegment() {
    return this.filterPathSegment;
  }

  getPaginationVars() {
    return {
      page: this.getPage(),
      size: this.getPageSize(),
    };
  }

  getPage() {
    const segment = this.pagePathSegment;
    return toInt(segment) || 1;
  }

  getPageSize() {
    return toInt(this.query.pageSize) || config.defaultPageSize;
  }

  // Sort

  getSortVars() {
    const { sort } = this.query;
    if (!sort) return undefined;
    return config.sortMap[sort] || undefined;
  }

  // Helpers

  getCategoryPathSegments() {
    return this.categoryPathSegments;
  }

  getFilterValue(key: UrlFilters) {
    const index = config.filter.positions.indexOf(key);

    if (index < 0) {
      throw new Error(`Unknown field: ${key}`);
    }

    const filterSegment = this.filterPathSegment;
    if (!filterSegment) return undefined;
    const value = filterSegment.split(config.separators.filter)[index];
    if (!value) return undefined;

    let placeholder = config.filter.placeholders[key];
    const prefix = config.filter.prefixes[key];
    if (prefix) {
      placeholder = `${prefix}${placeholder}`;
    }

    if (value === placeholder) return undefined;
    return value;
  }

  getFilterValues(key: UrlFilters) {
    const value = this.getFilterValue(key);
    if (!value) return [];
    return value.split(config.separators.item).sort();
  }
}
